import React, { useState, useEffect } from 'react';
import axios from 'axios';

// ── Kreator pierwszego uruchomienia mailmanagera (OPENkontakt) ────────────────
// Nakładka overlay pokazywana gdy company.onboarding_done === false.
// Wzorowany GRAFICZNIE i FUNKCJONALNIE na IdealnySklep OnboardingWizard.tsx,
// ale logika i kroki dla mailmanagera (Allegro).
//
// ETAP 5: 6 ekranów — powitanie + Allegro + e-mail + godziny + auto-odpowiedzi + gotowe.
// Krok profil sprzedażowy dokładany w etapie 6.
//
// Props:
//   onDone   - callback zamykający kreator (ustawia lokalny stan w Home)
//   isMobile - responsywność

const COLOR = '#15803d';
const ALLEGRO_PENDING_KEY = 'mm_onb_allegro_pending';
const DAY_LABELS: [string, number][] = [['Pon',0],['Wt',1],['Śr',2],['Czw',3],['Pt',4],['Sob',5],['Nd',6]];
const DAY_FULL = ['poniedziałek','wtorek','środa','czwartek','piątek','sobota','niedziela'];
const DAY_FULL_ACC = ['poniedziałku','wtorku','środy','czwartku','piątku','soboty','niedzieli']; // dopełniacz do "od...do"

// Opis dni roboczych pełnymi nazwami. Ciągły zakres → "od poniedziałku do piątku".
// Nieciągłe → lista "poniedziałek, środa, piątek". Pojedynczy → "poniedziałek".
function describeDays(days: number[]): string {
  const sorted = days.slice().sort((a, b) => a - b);
  if (sorted.length === 0) return '';
  // sprawdz ciaglosc
  const isContiguous = sorted.every((d, i) => i === 0 || d === sorted[i - 1] + 1);
  if (sorted.length === 1) return DAY_FULL[sorted[0]];
  if (isContiguous) return `od ${DAY_FULL_ACC[sorted[0]]} do ${DAY_FULL_ACC[sorted[sorted.length - 1]]}`;
  // nieciagle: lista pelnych nazw, ostatnia z "i"
  const names = sorted.map(d => DAY_FULL[d]);
  return names.slice(0, -1).join(', ') + ' i ' + names[names.length - 1];
}

// Szablony domyślnych auto-odpowiedzi (z podstawieniem {DNI} {OD} {DO}).
function buildAfterhours(daysDesc: string, from: string, to: string): string {
  return `Dzień dobry,
Zgłoszenie zostało zarejestrowane w naszym systemie. Odpowiemy najszybciej jak to będzie możliwe.
Pracujemy ${daysDesc} w godzinach ${from}-${to}.
Pozdrawiamy.`;
}
function buildWorkhours(daysDesc: string, from: string, to: string): string {
  return `Dziękujemy za kontakt! Potwierdzamy otrzymanie Twojego zapytania. Zrobimy wszystko, co w naszej mocy, aby odpowiedzieć Ci priorytetowo.
Nasz zespół pracuje ${daysDesc} w godzinach ${from}-${to}.`;
}
const ALERT_DEFAULT = `Dziękujemy za wiadomość!
Otrzymaliśmy Twoje zapytanie i jest ono w kolejce do obsługi.
Chociaż staramy się odpowiadać jak najszybciej, obecnie czas oczekiwania może być wydłużony.
Prosimy o nieprzesyłanie tej samej wiadomości ponownie, ponieważ może to dodatkowo opóźnić proces.
Dziękujemy za cierpliwość i zrozumienie.`;

interface AllegroAccount { id: string; name: string; expires_at: string | null; token_invalid: boolean; }
interface ImapAccount { id: string; name: string; host: string; port: string; login: string; active: boolean; smtp_host?: string; smtp_port?: string; }

export default function MMOnboardingWizard({ onDone, isMobile }: { onDone: () => void; isMobile: boolean }) {
  // Po powrocie z OAuth Allegro (znacznik w localStorage) wracamy na ekran Allegro (step=1)
  // z banerem gratulacji — a nie na powitanie (step=0), zeby klient widzial sukces.
  const returnedFromAllegro = (() => {
    try { return localStorage.getItem(ALLEGRO_PENDING_KEY) === '1'; } catch { return false; }
  })();

  const [step, setStep] = useState(returnedFromAllegro ? 1 : 0);
  const [justConnectedAllegro, setJustConnectedAllegro] = useState(returnedFromAllegro);
  const [saving, setSaving] = useState(false);

  // ── stan kroku Allegro ──
  const [allegroAccounts, setAllegroAccounts] = useState<AllegroAccount[]>([]);
  const [allegroLoading, setAllegroLoading] = useState(true);

  // ── stan kroku e-mail ──
  const [imapAccounts, setImapAccounts] = useState<ImapAccount[]>([]);
  const [imapLoading, setImapLoading] = useState(true);
  const [imapForm, setImapForm] = useState({ name: '', login: '', password: '' });
  const [imapBusy, setImapBusy] = useState(false);
  const [imapError, setImapError] = useState('');
  const [imapAttempts, setImapAttempts] = useState(0);
  const [imapManual, setImapManual] = useState({ imap_host: '', imap_port: '993', smtp_host: '', smtp_port: '587' });

  // ── stan kroku godziny pracy ──
  const [workStart, setWorkStart] = useState('08:00');
  const [workEnd, setWorkEnd] = useState('16:00');
  const [workDays, setWorkDays] = useState<number[]>([0, 1, 2, 3, 4]);
  const [hoursSaved, setHoursSaved] = useState(false);
  const [hoursBusy, setHoursBusy] = useState(false);

  // ── stan kroku auto-odpowiedzi ──
  const [afterhoursText, setAfterhoursText] = useState('');
  const [workhoursText, setWorkhoursText] = useState('');
  const [alertText, setAlertText] = useState(ALERT_DEFAULT);
  const [autoRepliesTouched, setAutoRepliesTouched] = useState(false); // czy klient recznie edytowal
  const [autoRepliesSaved, setAutoRepliesSaved] = useState(false);
  const [autoRepliesBusy, setAutoRepliesBusy] = useState(false);

  // ── stan kroku Apilo ──
  const [apiloForm, setApiloForm] = useState({ url: '', client_id: '', client_secret: '', auth_code: '' });
  const [apiloStatus, setApiloStatus] = useState<{ configured: boolean; total_count?: number | null } | null>(null);
  const [apiloLoading, setApiloLoading] = useState(true);
  const [apiloBusy, setApiloBusy] = useState(false);
  const [apiloMsg, setApiloMsg] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  const TOTAL = 7; // ETAP 5+Apilo: ekrany 0..6 (powitanie, Allegro, e-mail, godziny, auto-odpowiedzi, Apilo, gotowe)

  // Wyczysc znacznik powrotu z Allegro (juz odczytany do stanu poczatkowego).
  useEffect(() => {
    if (returnedFromAllegro) {
      try { localStorage.removeItem(ALLEGRO_PENDING_KEY); } catch (e) { /* ignoruj */ }
    }
  }, [returnedFromAllegro]);

  // Pobierz konta Allegro przy montowaniu (do kroku Allegro).
  useEffect(() => {
    axios.get('/api/allegro/accounts')
      .then(r => { setAllegroAccounts(Array.isArray(r.data) ? r.data : []); setAllegroLoading(false); })
      .catch(() => { setAllegroAccounts([]); setAllegroLoading(false); });
  }, []);

  // Pobierz skrzynki e-mail przy montowaniu (do kroku e-mail).
  const loadImap = () => {
    axios.get('/api/settings/imap-accounts')
      .then(r => { setImapAccounts(Array.isArray(r.data) ? r.data : []); setImapLoading(false); })
      .catch(() => { setImapAccounts([]); setImapLoading(false); });
  };
  useEffect(() => { loadImap(); }, []);
  // Sprawdz status Apilo przy montowaniu (do kroku Apilo).
  useEffect(() => { loadApiloStatus(); }, []);

  // Pobierz obecne godziny pracy przy montowaniu (do kroku godziny).
  useEffect(() => {
    axios.get('/api/settings/app')
      .then(r => {
        const d = r.data || {};
        if (d.work_start) setWorkStart(d.work_start);
        if (d.work_end) setWorkEnd(d.work_end);
        if (d.work_days) {
          const parsed = String(d.work_days).split(',').filter((x: string) => x.trim() !== '').map(Number);
          if (parsed.length) setWorkDays(parsed);
        }
      })
      .catch(() => { /* zostaw domyslne 08:00-16:00 pon-pt */ });
  }, []);

  // Gdy klient NIE edytowal recznie, tresci auto-odpowiedzi sa generowane z aktualnych godzin/dni.
  // (przelicza sie gdy zmienia sie godziny/dni w kroku 4 — chyba ze klient juz cos wpisal recznie).
  useEffect(() => {
    if (autoRepliesTouched) return;
    const daysDesc = describeDays(workDays);
    setAfterhoursText(buildAfterhours(daysDesc, workStart, workEnd));
    setWorkhoursText(buildWorkhours(daysDesc, workStart, workEnd));
  }, [workStart, workEnd, workDays, autoRepliesTouched]);

  // Podpięcie Allegro — dokładnie ten sam mechanizm co panel Settings.
  const connectAllegro = () => {
    const token = localStorage.getItem('token') || '';
    (window as any).__allowNavigation = true;
    try { localStorage.setItem(ALLEGRO_PENDING_KEY, '1'); } catch (e) { /* ignoruj */ }
    window.location.href = '/api/allegro/login?token=' + token;
  };

  // Zapis konta IMAP (POST) + dopisanie SMTP (PATCH). Wspolne dla trybu auto i recznego.
  const saveImapAccount = async (host: string, port: string, smtpHost: string, smtpPort: string) => {
    const created = await axios.post('/api/settings/imap-accounts', {
      name: imapForm.name, host, port, login: imapForm.login, password: imapForm.password,
    });
    const newId = created.data?.id;
    if (newId && (smtpHost || smtpPort)) {
      try {
        await axios.patch('/api/settings/imap-accounts/' + newId, {
          name: imapForm.name, host, port, login: imapForm.login, password: imapForm.password,
          smtp_host: smtpHost || '', smtp_port: smtpPort || '587',
        });
      } catch (e) { /* SMTP opcjonalny — nie blokuj sukcesu */ }
    }
  };

  const connectImapAuto = async () => {
    setImapError('');
    if (!imapForm.name.trim() || !imapForm.login.trim() || !imapForm.password) {
      setImapError('Podaj nazwę, adres e-mail i hasło.');
      return;
    }
    setImapBusy(true);
    try {
      const det = await axios.post('/api/email/detect-config', {
        host: 'auto', port: '993', login: imapForm.login, password: imapForm.password,
      });
      const d = det.data || {};
      if (d.status === 'ok' && d.detected_host) {
        await saveImapAccount(d.detected_host, d.detected_port || '993',
          d.detected_smtp_host || '', d.detected_smtp_port || '587');
        setImapForm({ name: '', login: '', password: '' });
        setImapAttempts(0);
        loadImap();
      } else {
        const attempts = imapAttempts + 1;
        setImapAttempts(attempts);
        if (attempts < 2) {
          setImapError('Nie udało się połączyć. Najczęstsza przyczyna to literówka w adresie e-mail albo błędne hasło. Sprawdź jedno i drugie — to hasło do Twojej skrzynki e-mail (u niektórych dostawców trzeba wygenerować „hasło aplikacji"), a nie hasło do OPENkontakt.');
        } else {
          setImapError('Nadal nie działa. Jeśli adres e-mail i hasło są na pewno poprawne, wpisz dane serwera ręcznie poniżej.');
        }
      }
    } catch (e: any) {
      const attempts = imapAttempts + 1;
      setImapAttempts(attempts);
      if (attempts < 2) {
        setImapError('Nie udało się połączyć. Najczęstsza przyczyna to literówka w adresie e-mail albo błędne hasło. Sprawdź jedno i drugie — to hasło do Twojej skrzynki e-mail (u niektórych dostawców trzeba wygenerować „hasło aplikacji"), a nie hasło do OPENkontakt.');
      } else {
        setImapError('Nadal nie działa. Jeśli adres e-mail i hasło są na pewno poprawne, wpisz dane serwera ręcznie poniżej.');
      }
    }
    setImapBusy(false);
  };

  const connectImapManual = async () => {
    setImapError('');
    if (!imapForm.name.trim() || !imapForm.login.trim() || !imapForm.password) {
      setImapError('Podaj nazwę, adres e-mail i hasło.');
      return;
    }
    if (!imapManual.imap_host.trim()) {
      setImapError('Podaj adres serwera IMAP.');
      return;
    }
    setImapBusy(true);
    try {
      const test = await axios.post('/api/email/test-connection', {
        host: imapManual.imap_host, port: imapManual.imap_port || '993',
        login: imapForm.login, password: imapForm.password,
      });
      if (test.data?.status === 'ok') {
        await saveImapAccount(imapManual.imap_host, imapManual.imap_port || '993',
          imapManual.smtp_host || '', imapManual.smtp_port || '587');
        setImapForm({ name: '', login: '', password: '' });
        setImapManual({ imap_host: '', imap_port: '993', smtp_host: '', smtp_port: '587' });
        setImapAttempts(0);
        loadImap();
      } else {
        setImapError('Nie udało się połączyć z podanym serwerem. Sprawdź adres serwera IMAP, port oraz hasło.');
      }
    } catch (e: any) {
      setImapError(e?.response?.data?.detail || 'Nie udało się połączyć z podanym serwerem. Sprawdź adres serwera IMAP, port oraz hasło.');
    }
    setImapBusy(false);
  };

  // Zapis godzin pracy + dni (work_start/work_end/work_days).
  const saveWorkHours = async () => {
    setHoursBusy(true);
    setHoursSaved(false);
    try {
      const days = workDays.slice().sort((a, b) => a - b).join(',');
      await Promise.all([
        axios.post('/api/settings/app/work_start', { value: workStart }),
        axios.post('/api/settings/app/work_end', { value: workEnd }),
        axios.post('/api/settings/app/work_days', { value: days }),
      ]);
      setHoursSaved(true);
    } catch (e) { /* nie blokuj — pozwol przejsc dalej */ }
    setHoursBusy(false);
  };

  const toggleDay = (d: number) => {
    setHoursSaved(false);
    setWorkDays(prev => prev.includes(d) ? prev.filter(x => x !== d) : [...prev, d]);
  };

  // Zapis auto-odpowiedzi: 3 tresci (PL) + 3 wlaczniki=true + alert_hours=4, potem tlumaczenia.
  const saveAutoReplies = async () => {
    setAutoRepliesBusy(true);
    setAutoRepliesSaved(false);
    try {
      // 1) zapisz tresci PL + wlaczniki + alert_hours
      await Promise.all([
        axios.post('/api/settings/app/auto_reply', { value: afterhoursText }),
        axios.post('/api/settings/app/workhours_reply', { value: workhoursText }),
        axios.post('/api/settings/app/alert_reply_text', { value: alertText }),
        axios.post('/api/settings/app/afterhours_reply_enabled', { value: 'true' }),
        axios.post('/api/settings/app/workhours_reply_enabled', { value: 'true' }),
        axios.post('/api/settings/app/auto_reply_enabled', { value: 'true' }),
        axios.post('/api/settings/app/alert_hours', { value: '4' }),
      ]);
      // 2) wygeneruj tlumaczenia (kazde osobno; nie blokuj gdy ktores padnie)
      await Promise.all([
        axios.get('/api/settings/app/auto-reply-translate').catch(() => {}),
        axios.get('/api/settings/app/workhours-reply-translate').catch(() => {}),
        axios.get('/api/settings/app/alert-reply-translate').catch(() => {}),
      ]);
      setAutoRepliesSaved(true);
    } catch (e) { /* nie blokuj przejscia dalej */ }
    setAutoRepliesBusy(false);
  };

  // Zakończenie/pominięcie — zapis flagi w firmie.
  // ── Apilo: status + polaczenie (te same endpointy co ApiloModule) ──
  const loadApiloStatus = async () => {
    setApiloLoading(true);
    try {
      const r = await axios.get('/api/settings/apilo');
      setApiloStatus(r.data);
    } catch { setApiloStatus({ configured: false }); }
    setApiloLoading(false);
  };
  const connectApilo = async () => {
    if (!apiloForm.url || !apiloForm.client_id || !apiloForm.client_secret || !apiloForm.auth_code) {
      setApiloMsg({ kind: 'err', text: 'Wype\u0142nij wszystkie pola.' });
      return;
    }
    setApiloBusy(true);
    setApiloMsg(null);
    try {
      const r = await axios.post('/api/settings/apilo/connect', apiloForm);
      setApiloStatus(r.data);
      setApiloForm({ url: '', client_id: '', client_secret: '', auth_code: '' });
      const cnt = r.data?.total_count;
      setApiloMsg({ kind: 'ok', text: cnt != null ? `Po\u0142\u0105czono z Apilo \u2014 ${cnt.toLocaleString('pl-PL')} zam\u00f3wie\u0144.` : 'Po\u0142\u0105czono z Apilo.' });
    } catch (e: any) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || 'Nie uda\u0142o si\u0119 po\u0142\u0105czy\u0107. Sprawd\u017a dane i czy kod autoryzacji nie wygas\u0142.';
      setApiloMsg({ kind: 'err', text: detail });
    }
    setApiloBusy(false);
  };

  const markDone = async () => {
    try { await axios.post('/companies/onboarding-done'); } catch (e) { /* nie blokuj */ }
  };

  const next = () => setStep(s => Math.min(s + 1, TOTAL - 1));

  const finish = async () => {
    setSaving(true);
    await markDone();
    setSaving(false);
    onDone();
  };

  const skip = async () => {
    setSaving(true);
    await markDone();
    setSaving(false);
    onDone();
  };

  // ── style (1:1 z IdealnySklep OnboardingWizard) ───────────────────────────────
  const overlay: React.CSSProperties = {
    position: 'fixed', inset: 0, zIndex: 10001,
    background: 'rgba(15,23,42,0.55)', backdropFilter: 'blur(2px)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: isMobile ? 12 : 24,
  };
  const card: React.CSSProperties = {
    background: '#fff', borderRadius: 16, width: '100%', maxWidth: 560,
    boxShadow: '0 20px 60px rgba(0,0,0,0.3)', overflow: 'hidden',
    display: 'flex', flexDirection: 'column', maxHeight: '92vh',
  };
  const headerStyle: React.CSSProperties = {
    padding: isMobile ? '14px 18px' : '18px 24px', borderBottom: '1px solid #eef2f7',
    display: 'flex', alignItems: 'center', gap: 10,
  };
  const bodyStyle: React.CSSProperties = {
    padding: isMobile ? '20px 18px' : '28px 32px', overflowY: 'auto', flex: 1,
  };
  const footer: React.CSSProperties = {
    padding: isMobile ? '14px 18px' : '16px 24px', borderTop: '1px solid #eef2f7',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
  };
  const primaryBtn: React.CSSProperties = {
    padding: '11px 22px', borderRadius: 10, border: 'none', background: COLOR,
    color: '#fff', fontSize: 15, fontWeight: 600, cursor: 'pointer', flexShrink: 0,
  };
  const ghostBtn: React.CSSProperties = {
    padding: '8px 12px', borderRadius: 8, border: 'none', background: 'transparent',
    color: '#9ca3af', fontSize: 12, cursor: 'pointer',
  };
  const inputStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #d1d5db',
    fontSize: 14, marginBottom: 10, boxSizing: 'border-box',
  };
  const textareaStyle: React.CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 9, border: '1px solid #d1d5db',
    fontSize: 13, marginBottom: 6, boxSizing: 'border-box', minHeight: 90, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.4,
  };
  const h1: React.CSSProperties = { fontSize: isMobile ? 20 : 24, fontWeight: 700, color: '#111827', margin: '0 0 8px' };
  const sub: React.CSSProperties = { fontSize: 14, color: '#6b7280', margin: '0 0 20px', lineHeight: 1.5 };
  const fieldLabel: React.CSSProperties = { fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6, marginTop: 4 };

  // ── pasek postępu (kropki) ────────────────────────────────────────────────────
  const dots = (
    <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
      {Array.from({ length: TOTAL }).map((_, i) => (
        <span key={i} style={{
          width: i === step ? 20 : 8, height: 8, borderRadius: 4,
          background: i === step ? COLOR : (i < step ? '#86efac' : '#e5e7eb'),
          transition: 'all 0.2s',
        }} />
      ))}
    </div>
  );

  const hasAllegro = allegroAccounts.length > 0;
  const hasImap = imapAccounts.length > 0;
  const showManual = imapAttempts >= 2;

  const onEditAuto = (setter: (v: string) => void) => (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setAutoRepliesTouched(true);
    setAutoRepliesSaved(false);
    setter(e.target.value);
  };

  // ── ekrany ────────────────────────────────────────────────────────────────────
  // Otwiera asystenta AI z wiedza per krok (mechanizm: flaga w localStorage nasluchiwana w AIAssistant.tsx).
  const openAssistant = (context: string) => {
    try {
      localStorage.setItem('assistant_context', context);
      localStorage.setItem('assistant_open_request', String(Date.now()));
    } catch (e) { /* ignoruj */ }
  };
  const AssistantLink = ({ context }: { context: string }) => (
    <div style={{ textAlign: 'center', marginTop: 14 }}>
      <button type="button" onClick={() => openAssistant(context)}
        style={{ border: 'none', background: 'transparent', color: '#15803d', fontSize: 13, cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}>
        🤖 Masz pytania? Zapytaj asystenta AI
      </button>
    </div>
  );
  const screens = [
    // Ekran 0 — Powitanie
    (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 44, margin: '4px 0 12px' }}>👋</div>
        <h1 style={h1}>Witaj w OPENkontakt!</h1>
        <p style={sub}>
          Za chwilę skonfigurujemy Twoje konto krok po kroku — podłączymy Allegro i pocztę,
          ustawimy godziny pracy i automatyczne odpowiedzi. Dzięki temu OPENkontakt zacznie
          obsługiwać Twoich klientów Allegro i odda Ci najcenniejsze: czas.
        </p>
      </div>
    ),
    // Ekran 1 — Allegro
    (
      <div>
        {justConnectedAllegro && (
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 12, padding: '14px 16px', marginBottom: 18, textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 4 }}>🎉</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#166534' }}>Gratulacje! Konto Allegro podłączone</div>
            <div style={{ fontSize: 13, color: '#15803d', marginTop: 4 }}>Widzisz — to było proste. Przejdźmy dalej.</div>
          </div>
        )}
        <div style={{ fontSize: 40, marginBottom: 10, textAlign: 'center' }}>🛒</div>
        <h1 style={{ ...h1, textAlign: 'center' }}>{hasAllegro ? 'Konta Allegro' : 'Podłącz konto Allegro'}</h1>
        <p style={{ ...sub, textAlign: 'center' }}>
          {hasAllegro
            ? 'Świetnie — OPENkontakt zaczął już pobierać wiadomości od Twoich klientów Allegro. Możesz podłączyć kolejne konto albo przejść dalej.'
            : 'To serce OPENkontakt. Po podłączeniu zaczniemy pobierać wiadomości od Twoich klientów Allegro, a AI pomoże Ci na nie odpowiadać — szybciej i o każdej porze.'}
        </p>
        {allegroLoading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 12 }}>Sprawdzam…</div>
        ) : hasAllegro ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px' }}>
            {allegroAccounts.map(a => (
              <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#166534', marginBottom: 4 }}>
                <span>✓</span>
                <span style={{ fontWeight: 600 }}>{a.name}</span>
                {a.token_invalid && <span style={{ color: '#b45309', fontSize: 12 }}>(wymaga ponownej autoryzacji)</span>}
              </div>
            ))}
            <button onClick={connectAllegro} style={{ marginTop: 8, padding: 0, border: 'none', background: 'transparent', color: COLOR, fontSize: 13, cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}>
              Podłącz kolejne konto Allegro
            </button>
          </div>
        ) : (
          <div style={{ textAlign: 'center' }}>
            <button onClick={connectAllegro} style={{ ...primaryBtn, padding: '13px 28px', fontSize: 16 }}>
              Podłącz Allegro
            </button>
            <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 12 }}>
              Przeniesiemy Cię na Allegro, aby autoryzować połączenie. Wrócisz tu automatycznie.
            </p>
          </div>
        )}
        <AssistantLink context="settings:allegro" />
      </div>
    ),
    // Ekran 2 — Skrzynka e-mail
    (
      <div>
        <div style={{ fontSize: 40, marginBottom: 10, textAlign: 'center' }}>✉️</div>
        <h1 style={{ ...h1, textAlign: 'center' }}>{hasImap ? 'Skrzynki e-mail' : 'Podłącz skrzynkę e-mail'}</h1>
        <p style={{ ...sub, textAlign: 'center' }}>
          {hasImap
            ? 'Świetnie — OPENkontakt będzie obsługiwał także wiadomości e-mail. Możesz podłączyć kolejną skrzynkę albo przejść dalej.'
            : 'Poza Allegro OPENkontakt może obsługiwać e-maile od klientów. Podaj dane skrzynki — resztę (serwery) wykryjemy automatycznie. Ten krok jest opcjonalny.'}
        </p>
        {imapLoading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 12 }}>Sprawdzam…</div>
        ) : (
          <div>
            {hasImap && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                {imapAccounts.map(a => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14, color: '#166534', marginBottom: 4 }}>
                    <span>✓</span>
                    <span style={{ fontWeight: 600 }}>{a.name}</span>
                    <span style={{ color: '#15803d', fontSize: 12 }}>({a.login})</span>
                  </div>
                ))}
              </div>
            )}
            <input style={inputStyle} placeholder="Nazwa (np. Poczta firmowa)" value={imapForm.name}
              onChange={e => setImapForm({ ...imapForm, name: e.target.value })} />
            <input style={inputStyle} placeholder="Adres e-mail" value={imapForm.login} autoComplete="off"
              onChange={e => setImapForm({ ...imapForm, login: e.target.value })} />
            <input style={inputStyle} placeholder="Hasło do skrzynki e-mail" type="password" value={imapForm.password} autoComplete="new-password"
              onChange={e => setImapForm({ ...imapForm, password: e.target.value })} />
            {imapError && <div style={{ color: showManual ? '#b45309' : '#dc2626', fontSize: 13, marginBottom: 10, lineHeight: 1.4 }}>{imapError}</div>}

            {showManual && (
              <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 10 }}>Ustawienia zaawansowane (serwery ręcznie)</div>
                <input style={inputStyle} placeholder="Serwer IMAP (np. imap.twojadomena.pl)" value={imapManual.imap_host}
                  onChange={e => setImapManual({ ...imapManual, imap_host: e.target.value })} />
                <input style={inputStyle} placeholder="Port IMAP (zwykle 993)" value={imapManual.imap_port}
                  onChange={e => setImapManual({ ...imapManual, imap_port: e.target.value })} />
                <input style={inputStyle} placeholder="Serwer SMTP (np. smtp.twojadomena.pl)" value={imapManual.smtp_host}
                  onChange={e => setImapManual({ ...imapManual, smtp_host: e.target.value })} />
                <input style={inputStyle} placeholder="Port SMTP (zwykle 587)" value={imapManual.smtp_port}
                  onChange={e => setImapManual({ ...imapManual, smtp_port: e.target.value })} />
              </div>
            )}

            <button onClick={showManual ? connectImapManual : connectImapAuto} disabled={imapBusy}
              style={{ ...primaryBtn, width: '100%', opacity: imapBusy ? 0.7 : 1 }}>
              {imapBusy ? 'Łączę…' : (showManual ? 'Sprawdź i zapisz ręcznie' : (hasImap ? 'Podłącz kolejną skrzynkę' : 'Sprawdź i podłącz'))}
            </button>
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>
              🔒 Hasło jest szyfrowane i używane wyłącznie do połączenia z Twoją skrzynką.
            </p>
          </div>
        )}
        <AssistantLink context="settings:imap" />
      </div>
    ),
    // Ekran 3 — Godziny pracy + dni robocze
    (
      <div>
        <div style={{ fontSize: 40, marginBottom: 10, textAlign: 'center' }}>🕐</div>
        <h1 style={{ ...h1, textAlign: 'center' }}>Godziny pracy</h1>
        <p style={{ ...sub, textAlign: 'center' }}>
          Ustaw, kiedy pracujesz. OPENkontakt użyje tego do automatycznych odpowiedzi —
          inaczej reaguje w godzinach pracy, inaczej poza nimi.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div>
            <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Początek pracy</label>
            <input type="time" value={workStart} onChange={e => { setWorkStart(e.target.value); setHoursSaved(false); }} style={inputStyle} />
          </div>
          <div>
            <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 4 }}>Koniec pracy</label>
            <input type="time" value={workEnd} onChange={e => { setWorkEnd(e.target.value); setHoursSaved(false); }} style={inputStyle} />
          </div>
        </div>
        <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 6 }}>Dni robocze</label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 18 }}>
          {DAY_LABELS.map(([lbl, d]) => {
            const active = workDays.includes(d);
            return (
              <button key={d} type="button" onClick={() => toggleDay(d)}
                style={{ padding: '8px 14px', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer',
                  border: active ? '1px solid #15803d' : '1px solid #d1d5db',
                  background: active ? COLOR : '#fff', color: active ? '#fff' : '#6b7280' }}>
                {lbl}
              </button>
            );
          })}
        </div>
        <button onClick={saveWorkHours} disabled={hoursBusy}
          style={{ ...primaryBtn, width: '100%', opacity: hoursBusy ? 0.7 : 1 }}>
          {hoursBusy ? 'Zapisuję…' : (hoursSaved ? '✓ Zapisano' : 'Zapisz godziny pracy')}
        </button>
        <AssistantLink context="settings:hours" />
      </div>
    ),
    // Ekran 4 — Automatyczne odpowiedzi
    (
      <div>
        <div style={{ fontSize: 40, marginBottom: 10, textAlign: 'center' }}>💬</div>
        <h1 style={{ ...h1, textAlign: 'center' }}>Automatyczne odpowiedzi</h1>
        <p style={{ ...sub, textAlign: 'center' }}>
          OPENkontakt może od razu odpisać klientowi, żeby wiedział, że jego wiadomość dotarła.
          Przygotowaliśmy gotowe treści z Twoimi godzinami pracy — możesz je zostawić lub zmienić.
          Zapiszemy je też w innych językach, dla klientów zagranicznych.
        </p>

        <label style={fieldLabel}>Poza godzinami pracy</label>
        <textarea style={textareaStyle} value={afterhoursText} onChange={onEditAuto(setAfterhoursText)} />

        <label style={fieldLabel}>W godzinach pracy</label>
        <textarea style={textareaStyle} value={workhoursText} onChange={onEditAuto(setWorkhoursText)} />

        <label style={fieldLabel}>Gdy wiadomość długo czeka (po 4 godzinach)</label>
        <textarea style={textareaStyle} value={alertText} onChange={onEditAuto(setAlertText)} />

        <button onClick={saveAutoReplies} disabled={autoRepliesBusy}
          style={{ ...primaryBtn, width: '100%', opacity: autoRepliesBusy ? 0.7 : 1, marginTop: 6 }}>
          {autoRepliesBusy ? 'Zapisuję i tłumaczę…' : (autoRepliesSaved ? '✓ Zapisano' : 'Zapisz auto-odpowiedzi')}
        </button>
        <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>
          Auto-odpowiedzi zostaną włączone. Zawsze zmienisz je później w Ustawieniach.
        </p>
        <AssistantLink context="settings:hours" />
      </div>
    ),
    // Ekran 5 — Apilo (wyszukiwarka zamowien)
    (
      <div>
        <div style={{ fontSize: 40, marginBottom: 10, textAlign: 'center' }}>{'\uD83D\uDD0D'}</div>
        <h1 style={{ ...h1, textAlign: 'center' }}>{apiloStatus?.configured ? 'Apilo pod\u0142\u0105czone' : 'Pod\u0142\u0105cz Apilo'}</h1>
        <p style={{ ...sub, textAlign: 'center' }}>
          {apiloStatus?.configured
            ? '\u015awietnie \u2014 zam\u00f3wienia z Apilo b\u0119dziesz wyszukiwa\u0107 b\u0142yskawicznie po nazwisku, mie\u015bcie, telefonie czy numerze. Mo\u017cesz przej\u015b\u0107 dalej.'
            : 'Podepnij Apilo, a b\u0119dziesz wyszukiwa\u0107 zam\u00f3wienia po nazwisku, mie\u015bcie, telefonie czy fragmencie numeru \u2014 czego wbudowana wyszukiwarka Apilo nie potrafi. Ten krok jest opcjonalny.'}
        </p>
        {apiloLoading ? (
          <div style={{ textAlign: 'center', color: '#9ca3af', fontSize: 13, padding: 12 }}>Sprawdzam\u2026</div>
        ) : apiloStatus?.configured ? (
          <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px', textAlign: 'center' }}>
            <div style={{ fontSize: 32, marginBottom: 4 }}>{'\u2705'}</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#166534' }}>Po\u0142\u0105czono z Apilo</div>
            {apiloStatus.total_count != null && (
              <div style={{ fontSize: 13, color: '#15803d', marginTop: 4 }}>{apiloStatus.total_count.toLocaleString('pl-PL')} zam\u00f3wie\u0144 w bazie</div>
            )}
          </div>
        ) : (
          <div>
            <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: '#166534', marginBottom: 8 }}>Sk\u0105d wzi\u0105\u0107 dane:</div>
              {[
                'W panelu Apilo wejd\u017a w Administracja \u2192 Klucze API Apilo \u2192 Nowa aplikacja REST API.',
                'Skopiuj Adres API, Client ID i Client Secret.',
                'Wygeneruj jednorazowy kod autoryzacji \u2014 po po\u0142\u0105czeniu dost\u0119p odnawia si\u0119 sam.',
              ].map((t, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13, color: '#15803d', marginBottom: 6 }}>
                  <span style={{ flexShrink: 0, fontWeight: 700, color: COLOR }}>{i + 1}.</span><span>{t}</span>
                </div>
              ))}
            </div>
            {apiloMsg && (
              <div style={{ padding: '10px 12px', borderRadius: 9, marginBottom: 12, fontSize: 13,
                background: apiloMsg.kind === 'ok' ? '#f0fdf4' : '#fef2f2',
                color: apiloMsg.kind === 'ok' ? '#15803d' : '#dc2626',
                border: '1px solid ' + (apiloMsg.kind === 'ok' ? '#bbf7d0' : '#fecaca') }}>
                {apiloMsg.text}
              </div>
            )}
            <label style={fieldLabel}>Adres API (endpoint)</label>
            <input style={inputStyle} value={apiloForm.url} onChange={e => setApiloForm({ ...apiloForm, url: e.target.value })} placeholder="https://twojafirma.apilo.com" />
            <label style={fieldLabel}>Client ID</label>
            <input style={inputStyle} value={apiloForm.client_id} onChange={e => setApiloForm({ ...apiloForm, client_id: e.target.value })} placeholder="np. 2" />
            <label style={fieldLabel}>Client Secret</label>
            <input style={inputStyle} value={apiloForm.client_secret} onChange={e => setApiloForm({ ...apiloForm, client_secret: e.target.value })} placeholder="klucz z panelu Apilo" />
            <label style={fieldLabel}>Kod autoryzacji</label>
            <input style={inputStyle} value={apiloForm.auth_code} onChange={e => setApiloForm({ ...apiloForm, auth_code: e.target.value })} placeholder="jednorazowy kod z panelu" />
            <button onClick={connectApilo} disabled={apiloBusy}
              style={{ ...primaryBtn, width: '100%', opacity: apiloBusy ? 0.7 : 1, marginTop: 6 }}>
              {apiloBusy ? '\u0141\u0105cz\u0119\u2026' : 'Po\u0142\u0105cz z Apilo'}
            </button>
            <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 10, textAlign: 'center' }}>
              Mo\u017cesz te\u017c pomin\u0105\u0107 i pod\u0142\u0105czy\u0107 Apilo p\u00f3\u017aniej w module Apilo.
            </p>
            <AssistantLink context="settings:apilo" />
          </div>
        )}
      </div>
    ),
    // Ekran 6 — Gotowe
    (
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 48, margin: '4px 0 12px' }}>🎉</div>
        <h1 style={h1}>Wszystko gotowe!</h1>
        <div style={{ textAlign: 'left', maxWidth: 380, margin: '18px auto 0', display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[
            'OPENkontakt zajmie się wiadomościami od klientów Allegro.',
            'Odpowiedzi AI oszczędzą Ci godziny pracy każdego dnia.',
            'Wszystko dopracujesz później w Ustawieniach.',
          ].map((p, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 14, color: '#374151' }}>
              <span style={{ color: COLOR, flexShrink: 0 }}>✓</span><span>{p}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  ];

  // ── przyciski stopki per ekran ────────────────────────────────────────────────
  const renderFooterMain = () => {
    if (step === 0) return <button style={primaryBtn} onClick={() => next()}>Zaczynajmy →</button>;
    if (step === TOTAL - 1) return <button style={primaryBtn} disabled={saving} onClick={finish}>Przejdź do panelu →</button>;
    return <button style={primaryBtn} onClick={() => next()}>Dalej →</button>;
  };

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={headerStyle}>
          <span style={{ fontSize: 13, fontWeight: 600, color: '#6b7280' }}>
            Krok {step + 1} z {TOTAL}
          </span>
          <div style={{ flex: 1 }} />
          {dots}
        </div>

        <div style={bodyStyle}>
          {screens[step]}
        </div>

        <div style={footer}>
          {step < TOTAL - 1
            ? <button style={ghostBtn} onClick={skip}>Pomiń na razie</button>
            : <span />}
          {renderFooterMain()}
        </div>
      </div>
    </div>
  );
}
